import React from 'react';
import { PrismicImage } from '@prismicio/react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { ModernSliderWrapper } from '@components/dashboard/SharedStyles';
import Colors from '@components/shared/Colors';
import { PageSlice } from '@contracts';
import Carousel from './Carousel';
import { SFDBodyLG, SFDBodyMD } from './Typography';
import useDesktop from './useDesktop';

const DESKTOP_BREAKPOINT = 1400;

interface Props {
  slice: PageSlice;
}

const StepSlider = ({ slice }: Props) => {
  const { t } = useTranslation();
  const headingTextKey = slice?.primary?.heading_text_key;
  const sliderItems = slice?.items;
  const isDesktop = useDesktop(DESKTOP_BREAKPOINT);
  const flickityOptions = {
    initialIndex: 0,
    groupCells: 1,
    draggable: !isDesktop,
    pageDots: true,
    prevNextButtons: false,
    contain: true,
    percentPosition: false,
    cellAlign: 'center',
    autoPlay: false,
    watchCSS: false
  };

  return (
    <Container>
      <HeadWrapper>
        <TitleWrapper>{t(headingTextKey)}</TitleWrapper>
      </HeadWrapper>
      <ItemsWrapper>
        <ModernSliderWrapper>
          <Carousel options={flickityOptions}>
            {sliderItems.map((item, index) => (
              <SlideWrapper key={index}>
                <PrismicImage field={item.image} />
                <SlideTextWrapper $background={item.background_color} $text={item.text_color}>
                  <SFDBodyLG $bold>{t(item.title_key)}</SFDBodyLG>
                  <SFDBodyMD>{t(item.text_key)}</SFDBodyMD>
                </SlideTextWrapper>
              </SlideWrapper>
            ))}
          </Carousel>
        </ModernSliderWrapper>
      </ItemsWrapper>
    </Container>
  );
};

export default StepSlider;

const Container = styled.div`
  color: ${Colors.navyBlue};
  display: flex;
  flex-direction: column;
  padding: 60px 120px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 50px 0;
    max-width: 400px;
    margin: 0 auto;
  }

  @media (max-width: 440px) {
    max-width: 300px;
  }
`;

const HeadWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const TitleWrapper = styled.div`
  color: ${Colors.navyBlue};
  font-family: 'Domaine';
  font-size: 36px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.48px;
  width: 720px;
  text-align: center;
  max-width: 100%;

  @media (max-width: 1200px) {
    font-size: 24px;
  }
`;

const ItemsWrapper = styled.div`
  .flickity-page-dots {
    display: none;
  }

  .carousel:after {
    content: 'flickity';
    display: none; /* hide :after */
  }

  .flickity-viewport {
    overflow: visible !important;
  }

  @media (max-width: ${DESKTOP_BREAKPOINT}px) {
    .flickity-page-dots {
      display: block;
    }
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  padding-bottom: 0px;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 300px;
  margin: 0 10px 20px;
  border-radius: 2px;
  overflow: hidden;
  border-radius: 2px;

  @media (max-width: 768px) {
    width: 400px;
    max-width: 100%;
  }

  @media (max-width: 440px) {
    width: 300px;
    max-width: 100%;
  }

  & img {
    width: 100%;
  }
`;

const SlideTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 30px;
  background: ${({ $background }) => $background ?? Colors.ecru50};
  color: ${({ $text }) => $text ?? Colors.navyBlue};
  min-height: 260px;

  @media (max-width: 768px) {
    min-height: 0;
  }
`;
